import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import {
  PATIENT_NOTES_MAX_LENGTH,
  genderOptions,
  patientListStatusDisplay,
  patientStatusChipSx,
  type PatientAssignee,
} from '../../constants/patient';
import StaffAssigneeDisplay from '../StaffAssigneeDisplay';
import { useAppSnackbar } from '../../snackbar/useAppSnackbar';
import OrganizationAutocomplete from './OrganizationAutocomplete';
import MedicalDevicesFields from './fields/MedicalDevicesFields';
import CareServiceFields from './fields/CareServiceFields';
import MedicalInsuranceFields from './fields/MedicalInsuranceFields';
import MedicalInfoRequestFields from './fields/MedicalInfoRequestFields';
import PendingDoneRadioGroup from './fields/PendingDoneRadioGroup';
import IntakeSectionCard from './IntakeSectionCard';
import IntakeFormRow from './IntakeFormRow';
import IntakeTextField from './IntakeTextField';
import IntakeDatePicker from './IntakeDatePicker';
import IntakeToggleGroup from './IntakeToggleGroup';
import { INTAKE_LABEL_WIDTH, intakeFieldSlot, intakePageMaxWidthSx } from './intakeFormLayout';
import { usePatientIntakeForm } from './usePatientIntakeForm';

type Props = {
  patientId?: number | null;
};

/** 病名・機器と医療保険情報を横並び（患者情報画面と同じ） */
const pairedRowSection = { xs: 12, md: 6, lg: 6 };

function calcAge(dob: string): string {
  if (!dob) return '';
  const d = dayjs(dob);
  if (!d.isValid()) return '';
  return `${dayjs().diff(d, 'year')}歳`;
}

/** 近日の日付に曜日を添える（アポ・初診の調整に有用） */
function weekdayJa(date: string): string {
  if (!date) return '';
  const d = dayjs(date);
  return d.isValid() ? d.locale('ja').format('dddd') : '';
}

/**
 * 選択中の担当者/医師が一覧（在職者など）に含まれない場合でも表示できるよう、
 * 現在値を候補に id 重複排除でマージする。
 */
function withSelected(options: PatientAssignee[], current: PatientAssignee | null): PatientAssignee[] {
  if (!current) return options;
  return options.some((o) => o.id === current.id) ? options : [current, ...options];
}

const PatientIntakeForm: React.FC<Props> = ({ patientId = null }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const [staffOptions, setStaffOptions] = useState<PatientAssignee[]>([]);
  const [physicianOptions, setPhysicianOptions] = useState<PatientAssignee[]>([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const {
    form,
    patchForm,
    id,
    loading,
    loadError,
    saving,
    saveError,
    setSaveError,
    lastSavedAt,
    status,
    saveDraft,
    completeIntake,
  } = usePatientIntakeForm(patientId ?? null);

  const isExisting = patientId != null;
  /** 新規作成・下書きの受付フロー */
  const isIntakeFlow = status === 'draft' || (status === null && patientId == null);
  /** 受付完了済み患者の編集 */
  const isCompletedEditFlow = status === 'new';

  useEffect(() => {
    // 担当者・医師は独立して取得（片方の失敗でもう片方が空にならないように）
    void (async () => {
      try {
        const res = await apiClient.get<PatientAssignee[]>('/api/staff-directory');
        setStaffOptions(res.data);
      } catch {
        setStaffOptions([]);
      }
    })();
    void (async () => {
      try {
        const res = await apiClient.get<PatientAssignee[]>('/api/staff-directory', {
          params: { role: '医師' },
        });
        setPhysicianOptions(res.data);
      } catch {
        setPhysicianOptions([]);
      }
    })();
  }, []);

  // 受付完了に必要な必須項目（すべて受付サマリーに集約）
  const requiredChecks = [
    { label: '患者名', filled: form.name.trim() !== '' },
    { label: '性別', filled: form.gender !== '' },
    { label: '生年月日', filled: form.dateOfBirth !== '' },
    { label: '同意書アポ日', filled: form.consentAppointmentDate !== '' },
    { label: '応対者', filled: form.consentAppointmentContact.trim() !== '' },
  ];
  const missing = requiredChecks.filter((c) => !c.filled);
  const statusDisplay = patientListStatusDisplay({
    status: status ?? 'draft',
    archived_at: null,
  });

  const handleComplete = async () => {
    setSaveError('');
    const ok = await completeIntake();
    if (ok) {
      showSnackbar({ message: '受付を完了しました', severity: 'success' });
      navigate(id != null ? `/patients/${id}` : '/patients');
    }
  };

  const handleDraftSave = async () => {
    setSaveError('');
    const savedId = await saveDraft();
    if (savedId != null) {
      showSnackbar({ message: '下書きを保存しました', severity: 'success' });
      navigate(`/patients/${savedId}`);
    }
  };

  const handleSave = async () => {
    setSaveError('');
    const savedId = await saveDraft();
    if (savedId != null) {
      showSnackbar({ message: '保存しました', severity: 'success' });
      navigate(`/patients/${savedId}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {loadError}
      </Alert>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', width: '100%', pb: 12, pr: { xs: 0.75, lg: 0 } }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'flex-start' },
            mb: 1.5,
            gap: 1,
            ...intakePageMaxWidthSx,
          }}
        >
          <Box sx={{ minWidth: 0, maxWidth: 475, width: '100%' }}>
            <Paper
              variant="outlined"
              sx={{
                width: '100%',
                maxWidth: 475,
                px: { xs: 1.5, sm: 2 },
                py: 1.25,
                borderRadius: 1.5,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <IntakeTextField
                    value={form.patientNumber}
                    onChange={(e) => patchForm({ patientNumber: e.target.value.replace(/\D/g, '') })}
                    placeholder="患者番号"
                    sx={{
                      mb: 0.5,
                      '& .MuiInputBase-input': { fontSize: '0.8125rem' },
                    }}
                  />
                  <IntakeTextField
                    id="intake-name"
                    autoFocus={!isExisting}
                    value={form.name}
                    onChange={(e) => patchForm({ name: e.target.value })}
                    placeholder="患者名"
                    sx={{
                      '& .MuiInputBase-input': { fontSize: '0.8125rem' },
                    }}
                  />
                  <IntakeTextField
                    value={form.nameKana}
                    onChange={(e) => patchForm({ nameKana: e.target.value })}
                    placeholder="フリガナ"
                    sx={{
                      mt: 0.25,
                      '& .MuiInputBase-input': { fontSize: '0.8125rem' },
                    }}
                  />
                </Box>
                <Chip
                  label={statusDisplay.label}
                  variant="outlined"
                  size="small"
                  sx={{ flexShrink: 0, ...patientStatusChipSx }}
                  color={statusDisplay.chipColor}
                />
              </Box>
              <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <IntakeToggleGroup
                  options={genderOptions}
                  value={form.gender}
                  onChange={(v) => patchForm({ gender: v })}
                  ariaLabel="性別"
                />
                <IntakeDatePicker
                  dob
                  value={form.dateOfBirth}
                  onChange={(v) => patchForm({ dateOfBirth: v })}
                  helperText={form.dateOfBirth ? `満${calcAge(form.dateOfBirth)}` : undefined}
                />
                <IntakeTextField
                  multiline
                  minRows={1}
                  value={form.address}
                  onChange={(e) => patchForm({ address: e.target.value })}
                  placeholder="住所"
                  inputProps={{ 'aria-label': '住所' }}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '0.8125rem' },
                  }}
                />
              </Box>
            </Paper>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', sm: 'block' } }} />
          {lastSavedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              最終保存: {lastSavedAt.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
        </Box>

        {saveError && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.25, ...intakePageMaxWidthSx }} onClose={() => setSaveError('')}>
            {saveError}
          </Alert>
        )}

        <Box sx={intakePageMaxWidthSx}>
          <Grid container spacing={1.5}>
          <IntakeSectionCard compact title="依頼・初診">
            <IntakeFormRow compact label="依頼日" required>
              <IntakeDatePicker
                value={form.requestedOn}
                clearable={false}
                onChange={(v) => patchForm({ requestedOn: v || form.requestedOn })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="訪問開始の目安">
              <IntakeTextField
                value={form.visitStartEstimate}
                onChange={(e) => patchForm({ visitStartEstimate: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="初診日">
              <IntakeDatePicker
                value={form.firstVisitDate}
                onChange={(v) => patchForm({ firstVisitDate: v })}
                helperText={form.firstVisitDate ? weekdayJa(form.firstVisitDate) : undefined}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="初診日同席者" align="flex-start">
              <IntakeTextField
                multiline
                minRows={1}
                value={form.firstVisitAttendees}
                onChange={(e) => patchForm({ firstVisitAttendees: e.target.value })}
                placeholder="ケアマネ・ご家族など"
              />
            </IntakeFormRow>
          </IntakeSectionCard>

          <IntakeSectionCard compact title="紹介元">
            <IntakeFormRow compact label="紹介元">
              <OrganizationAutocomplete
                kind="referral"
                entityLabel="紹介元"
                placeholder="機関を選択"
                value={form.referralSource}
                onChange={(org) => patchForm({ referralSource: org })}
                onPhoneSuggest={(phone) => patchForm({ referralPhone: phone })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="紹介元電話">
              <IntakeTextField
                value={form.referralPhone}
                onChange={(e) => patchForm({ referralPhone: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="担当者名">
              <IntakeTextField
                value={form.referralContactName}
                onChange={(e) => patchForm({ referralContactName: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="依頼の経緯" align="flex-start">
              <IntakeTextField
                multiline
                minRows={2}
                value={form.requestBackground}
                onChange={(e) => patchForm({ requestBackground: e.target.value })}
              />
            </IntakeFormRow>
          </IntakeSectionCard>

          <IntakeSectionCard compact title="書類依頼" size={pairedRowSection}>
            {(
              [
                ['faceSheetRequest', 'フェイスシート依頼'],
                ['carePlanRequest', 'ケアプラン依頼'],
              ] as const
            ).map(([key, label]) => (
              <PendingDoneRadioGroup
                key={key}
                label={label}
                value={form[key]}
                onChange={(v) => patchForm({ [key]: v })}
              />
            ))}
            <MedicalInfoRequestFields
              value={form.medicalInfoRequests}
              onChange={(medicalInfoRequests) => patchForm({ medicalInfoRequests })}
            />
          </IntakeSectionCard>

          <IntakeSectionCard compact title="同意書" size={pairedRowSection}>
            <IntakeFormRow compact label="同意書アポ日" required>
              <IntakeDatePicker
                required
                value={form.consentAppointmentDate}
                onChange={(v) => patchForm({ consentAppointmentDate: v })}
                helperText={
                  form.consentAppointmentDate
                    ? weekdayJa(form.consentAppointmentDate)
                    : ''
                }
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="応対者" required>
              <IntakeTextField
                value={form.consentAppointmentContact}
                onChange={(e) => patchForm({ consentAppointmentContact: e.target.value })}
                placeholder="同意書アポの相手"
              />
            </IntakeFormRow>
          </IntakeSectionCard>

          <IntakeSectionCard compact title="本人・パーキング">
            <IntakeFormRow compact label="患者電話">
              <IntakeTextField
                value={form.patientPhone}
                onChange={(e) => patchForm({ patientPhone: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="本人の状態" align="flex-start">
              <IntakeTextField
                multiline
                minRows={2}
                placeholder="認知面・ADL"
                value={form.patientCondition}
                onChange={(e) => patchForm({ patientCondition: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="パーキング" align="flex-start">
              <IntakeTextField
                multiline
                minRows={1}
                value={form.parkingInfo}
                onChange={(e) => patchForm({ parkingInfo: e.target.value })}
                placeholder="お家の近くの駐車場の有無など"
              />
            </IntakeFormRow>
          </IntakeSectionCard>

          <IntakeSectionCard compact title="家族">
            <IntakeFormRow compact label="家族構成" align="flex-start">
              <IntakeTextField
                multiline
                minRows={1}
                value={form.familyStructure}
                onChange={(e) => patchForm({ familyStructure: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="キーパーソン">
              <IntakeTextField
                value={form.keyPerson}
                onChange={(e) => patchForm({ keyPerson: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="キーパーソン電話">
              <IntakeTextField
                value={form.keyPersonPhone}
                onChange={(e) => patchForm({ keyPersonPhone: e.target.value })}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="キーパーソン住所" align="flex-start">
              <IntakeTextField
                multiline
                minRows={1}
                value={form.keyPersonAddress}
                onChange={(e) => patchForm({ keyPersonAddress: e.target.value })}
              />
            </IntakeFormRow>
          </IntakeSectionCard>

          <IntakeSectionCard compact title="医療者">
            <IntakeFormRow compact label="主治医">
              <Autocomplete
                fullWidth
                size="small"
                options={withSelected(physicianOptions, form.attendingPhysician)}
                value={form.attendingPhysician}
                onChange={(_, v) => patchForm({ attendingPhysician: v })}
                getOptionLabel={(o) => o.display_name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <StaffAssigneeDisplay assignee={option} size={28} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} {...intakeFieldSlot} placeholder="医師を選択" />
                )}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="業務担当者">
              <Autocomplete
                fullWidth
                size="small"
                options={withSelected(staffOptions, form.assignedStaff)}
                value={form.assignedStaff}
                onChange={(_, v) => patchForm({ assignedStaff: v })}
                getOptionLabel={(o) => o.display_name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <StaffAssigneeDisplay assignee={option} size={28} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} {...intakeFieldSlot} placeholder="担当者を選択" />
                )}
              />
            </IntakeFormRow>
            <IntakeFormRow compact label="かかりつけ医">
              <OrganizationAutocomplete
                kind="family_doctor"
                entityLabel="かかりつけ医"
                placeholder="医療機関を選択"
                value={form.familyDoctor}
                onChange={(org) => patchForm({ familyDoctor: org })}
              />
            </IntakeFormRow>
          </IntakeSectionCard>

          <IntakeSectionCard compact title="病名・機器" size={pairedRowSection}>
            {form.diagnoses.map((d, i) => (
              <IntakeFormRow compact key={i} label={i === 0 ? '病名' : `病名${i + 1}`}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <IntakeTextField
                    value={d}
                    onChange={(e) => {
                      const next = [...form.diagnoses];
                      next[i] = e.target.value;
                      patchForm({ diagnoses: next });
                    }}
                    sx={{ flex: 1 }}
                  />
                  {form.diagnoses.length > 1 && (
                    <IconButton
                      size="small"
                      aria-label="削除"
                      onClick={() =>
                        patchForm({ diagnoses: form.diagnoses.filter((_, j) => j !== i) })
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </IntakeFormRow>
            ))}
            {form.diagnoses.length < 10 && (
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => patchForm({ diagnoses: [...form.diagnoses, ''] })}
                sx={{ ml: INTAKE_LABEL_WIDTH, mb: 0.5 }}
              >
                病名を追加
              </Button>
            )}
            <MedicalDevicesFields
              value={form.medicalDevices}
              onChange={(medicalDevices) => patchForm({ medicalDevices })}
            />
          </IntakeSectionCard>

          <IntakeSectionCard compact title="医療保険情報" size={pairedRowSection}>
            <MedicalInsuranceFields form={form} patchForm={patchForm} />
          </IntakeSectionCard>

          <IntakeSectionCard compact title="介護・サービス" size={{ xs: 12 }}>
            <CareServiceFields form={form} patchForm={patchForm} />
          </IntakeSectionCard>

          <IntakeSectionCard compact title="特記事項" size={{ xs: 12 }}>
            <IntakeTextField
              multiline
              minRows={2}
              placeholder="懸念点など"
              value={form.notes}
              onChange={(e) => patchForm({ notes: e.target.value })}
              inputProps={{ maxLength: PATIENT_NOTES_MAX_LENGTH }}
            />
          </IntakeSectionCard>
        </Grid>
        </Box>
      </LocalizationProvider>

      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          zIndex: 10,
        }}
      >
        {isIntakeFlow &&
          (missing.length === 0 ? (
            <Chip color="success" variant="filled" size="small" label="必須項目OK" />
          ) : (
            <Tooltip title={`未入力: ${missing.map((m) => m.label).join('・')}`}>
              <Chip
                color="warning"
                variant="outlined"
                size="small"
                label={`受付完了まで あと${missing.length}項目`}
              />
            </Tooltip>
          ))}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: isIntakeFlow ? 0 : 'auto' }}>
          {isIntakeFlow && (
            <Button variant="outlined" disabled={saving} onClick={() => setCancelDialogOpen(true)}>
              キャンセル
            </Button>
          )}
          {isCompletedEditFlow && (id != null || patientId != null) && (
            <Button
              variant="outlined"
              disabled={saving}
              onClick={() => navigate(`/patients/${patientId ?? id}`)}
            >
              キャンセル
            </Button>
          )}
          {isCompletedEditFlow ? (
            <Button variant="contained" color="primary" disabled={saving} onClick={() => void handleSave()}>
              {saving ? '保存中…' : '保存'}
            </Button>
          ) : (
            <>
              <Button variant="outlined" disabled={saving} onClick={() => void handleDraftSave()}>
                {saving ? '保存中…' : '下書き保存'}
              </Button>
              <Tooltip
                title={missing.length > 0 ? `未入力: ${missing.map((m) => m.label).join('・')}` : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={missing.length > 0 || saving}
                    onClick={() => void handleComplete()}
                  >
                    受付完了
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Paper>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => !saving && setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>キャンセルしますか？</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            入力内容は保存されず、患者一覧に戻ります。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={saving} sx={{ minWidth: 100 }}>
            戻る
          </Button>
          <Button
            onClick={() => navigate('/patients')}
            variant="contained"
            disabled={saving}
            sx={{ minWidth: 100 }}
          >
            はい
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientIntakeForm;
